import type { OpLog } from '@panels/shared';
import type { RenderLimit } from './render.js';

/**
 * Replay-on-view (§9): a comic draws itself when you open it, using the per-point
 * timing captured at draw time. The pacing is compressed — real drawing takes
 * ages, and a reveal should feel snappy — but the *shape* of the timing is the
 * real one, which is why `t` is stored per point.
 */

export interface ReplayOptions {
  /** A panel should replay in about this long. Faster drawings stay their own speed. */
  targetMs?: number;
  /** Thinking pauses mid-stroke get capped to this, so nobody waits on a hesitation. */
  maxPointGapMs?: number;
  /** Beat between one stroke ending and the next starting. */
  interStrokeGapMs?: number;
}

export interface ReplayTimeline {
  /** Absolute, already-compressed time for each captured point. */
  times: Float64Array;
  opIndex: Int32Array;
  pointIndex: Int32Array;
  duration: number;
  /** True when there is nothing to animate (an empty panel). */
  empty: boolean;
}

const DEFAULTS: Required<ReplayOptions> = {
  targetMs: 2200,
  maxPointGapMs: 60,
  interStrokeGapMs: 140,
};

export function buildTimeline(log: OpLog | null, options: ReplayOptions = {}): ReplayTimeline {
  const { targetMs, maxPointGapMs, interStrokeGapMs } = { ...DEFAULTS, ...options };

  const times: number[] = [];
  const ops: number[] = [];
  const points: number[] = [];
  let clock = 0;

  if (log) {
    for (let i = 0; i < log.ops.length; i++) {
      const op = log.ops[i];
      if (op.kind !== 'stroke' || op.points.length === 0) continue;
      if (times.length > 0) clock += interStrokeGapMs;
      for (let j = 0; j < op.points.length; j++) {
        if (j > 0) {
          const delta = op.points[j][2] - op.points[j - 1][2];
          clock += Math.min(Math.max(delta, 0), maxPointGapMs);
        }
        times.push(clock);
        ops.push(i);
        points.push(j);
      }
    }
  }

  const duration = times.length > 0 ? times[times.length - 1] : 0;
  // Speed up long drawings; never slow down short ones.
  const speed = duration > targetMs ? duration / targetMs : 1;
  const scaled = new Float64Array(times.length);
  for (let i = 0; i < times.length; i++) scaled[i] = times[i] / speed;

  return {
    times: scaled,
    opIndex: Int32Array.from(ops),
    pointIndex: Int32Array.from(points),
    duration: duration / speed,
    empty: times.length === 0,
  };
}

/**
 * How much of the drawing exists at `elapsed` ms. Returns null before the first
 * point (nothing drawn yet); callers treat `elapsed >= duration` as complete.
 */
export function limitAt(timeline: ReplayTimeline, elapsed: number): RenderLimit | null {
  const { times } = timeline;
  if (times.length === 0) return null;
  if (elapsed >= timeline.duration) {
    const last = times.length - 1;
    return { op: timeline.opIndex[last], point: timeline.pointIndex[last] };
  }

  // Binary search for the last point whose time has arrived.
  let lo = 0;
  let hi = times.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid] <= elapsed) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (found < 0) return null;
  return { op: timeline.opIndex[found], point: timeline.pointIndex[found] };
}

/* ------------------------------------------------------ whole-comic timing */

/**
 * A whole comic's replay: panel after panel, each starting the moment the one
 * before it finishes. Every client builds this from the same op logs, so given
 * the scheduled start of a co-viewed replay (Round 3), everyone agrees on which
 * panel is drawing and how far along it is.
 */
export interface ComicSchedule {
  /** When each panel starts, in ms from the start of the comic. */
  starts: number[];
  durations: number[];
  total: number;
}

export function comicSchedule(logs: Array<OpLog | null>, options: ReplayOptions = {}): ComicSchedule {
  const starts: number[] = [];
  const durations: number[] = [];
  let clock = 0;
  for (const log of logs) {
    const duration = buildTimeline(log, options).duration;
    starts.push(clock);
    durations.push(duration);
    clock += duration;
  }
  return { starts, durations, total: clock };
}

export interface ComicPosition {
  /** The panel drawing now: its index, or the panel count once the comic is done. */
  panel: number;
  /** How far into that panel's replay (negative before the comic starts). */
  panelElapsed: number;
  done: boolean;
}

/** Where a comic's replay is `elapsed` ms after it started. A late joiner lands here. */
export function comicPositionAt(schedule: ComicSchedule, elapsed: number): ComicPosition {
  const count = schedule.starts.length;
  if (elapsed >= schedule.total) return { panel: count, panelElapsed: 0, done: true };
  if (elapsed < 0) return { panel: 0, panelElapsed: elapsed, done: false };
  for (let i = 0; i < count; i++) {
    if (elapsed < schedule.starts[i] + schedule.durations[i]) return { panel: i, panelElapsed: elapsed - schedule.starts[i], done: false };
  }
  return { panel: count, panelElapsed: 0, done: true };
}
